import { useQuery } from '@tanstack/react-query';
import {
  ChartBarSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { examApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import ModuleIntroPanel from '../components/help/ModuleIntroPanel';
import ModuleConnections from '../components/help/ModuleConnections';
import clsx from 'clsx';

const READINESS_COLORS: Record<string, string> = {
  HIGH: 'text-green-700 bg-green-100',
  MODERATE: 'text-yellow-700 bg-yellow-100',
  LOW: 'text-red-700 bg-red-100',
};

function getReadinessLevel(score: number): string {
  if (score >= 75) return 'HIGH';
  if (score >= 50) return 'MODERATE';
  return 'LOW';
}

export default function ExamReadinessPage() {
  const { teamId } = useTeamId();

  // Fetch the most recent or upcoming exam
  const { data: examsData, isLoading: examsLoading } = useQuery({
    queryKey: ['exams', teamId],
    queryFn: () => examApi.list(teamId!),
    enabled: !!teamId,
  });
  const exams = examsData?.data?.exams || examsData?.data || [];
  // Prefer: in-progress > planning with future date > planning with past date > most recent
  const now = Date.now();
  const isFuture = (e: any) => e.scheduledStartDate && new Date(e.scheduledStartDate).getTime() >= now;
  const upcomingExam =
    exams.find((e: any) => e.status === 'IN_PROGRESS') ||
    exams.find((e: any) => e.status === 'PLANNING' && isFuture(e)) ||
    exams.find((e: any) => e.status === 'PLANNING') ||
    exams[0];

  // Fetch readiness for the exam
  const { data: readinessData, isLoading: readinessLoading } = useQuery({
    queryKey: ['exam-readiness', teamId, upcomingExam?.id],
    queryFn: () => examApi.getReadiness(teamId!, upcomingExam.id),
    enabled: !!teamId && !!upcomingExam?.id,
  });
  const readiness = readinessData?.data;

  const isLoading = examsLoading || readinessLoading;

  return (
    <FeatureGate feature="exam_readiness" mode="lock">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exam Readiness</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track your institution's readiness for regulatory examinations
          </p>
        </div>

        <ModuleIntroPanel moduleKey="exam-readiness" />
        <ModuleConnections moduleKey="exam-readiness" />

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
          </div>
        ) : !upcomingExam ? (
          <div className="rounded-lg border bg-white p-12 text-center">
            <ChartBarSquareIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-3 text-sm font-semibold text-gray-900">No exams scheduled</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create an exam in Exam Management to track readiness.
            </p>
          </div>
        ) : (
          <>
            {/* Overall Score */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div className="col-span-1 rounded-lg border bg-white p-6">
                <p className="text-xs font-medium text-gray-500 uppercase">Readiness Score</p>
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="text-4xl font-bold text-gray-900">{readiness?.readinessScore ?? 0}%</p>
                  <span className={clsx(
                    'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                    READINESS_COLORS[getReadinessLevel(readiness?.readinessScore ?? 0)]
                  )}>
                    {getReadinessLevel(readiness?.readinessScore ?? 0)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-400">{upcomingExam.examType?.replace(/_/g, ' ')}</p>
              </div>

              <div className="rounded-lg border bg-white p-6">
                <p className="text-xs font-medium text-gray-500 uppercase">Documents Ready</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">
                  {readiness?.readyDocuments ?? 0} / {readiness?.totalDocuments ?? 0}
                </p>
                <p className="mt-1 text-xs text-gray-400">Prepared for examiner</p>
              </div>

              <div className="rounded-lg border bg-white p-6">
                <p className="text-xs font-medium text-gray-500 uppercase">Open Findings</p>
                <p className={clsx(
                  'mt-2 text-2xl font-bold',
                  (readiness?.openFindings ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                )}>
                  {readiness?.openFindings ?? 0}
                </p>
                <p className="mt-1 text-xs text-gray-400">Requiring remediation</p>
              </div>

              <div className="rounded-lg border bg-white p-6">
                {(() => {
                  const days = readiness?.daysUntilExam;
                  const isPastDue = days != null && days < 0;
                  const isUrgent = days != null && days >= 0 && days <= 14;
                  return (
                    <>
                      <p className="text-xs font-medium text-gray-500 uppercase">
                        {isPastDue ? 'Exam Past Due' : 'Days Until Exam'}
                      </p>
                      <p className={clsx(
                        'mt-2 text-2xl font-bold',
                        isPastDue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-gray-900'
                      )}>
                        {days == null ? '—' : isPastDue ? `${Math.abs(days)} days ago` : days}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {upcomingExam.scheduledStartDate
                          ? new Date(upcomingExam.scheduledStartDate).toLocaleDateString()
                          : 'Not scheduled'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Document Category Coverage */}
            {readiness?.categoryCoverage && (
              <div className="rounded-lg border bg-white">
                <div className="border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Document Preparation by Category</h3>
                </div>
                <div className="divide-y">
                  {Object.entries(readiness.categoryCoverage as Record<string, { total: number; ready: number }>).map(
                    ([category, data]) => {
                      const pct = data.total > 0 ? Math.round((data.ready / data.total) * 100) : 0;
                      return (
                        <div key={category} className="flex items-center gap-4 px-4 py-3">
                          <div className="flex-shrink-0">
                            {pct === 100 ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : pct > 0 ? (
                              <ClockIcon className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {category.replace(/_/g, ' ')}
                            </p>
                            <div className="mt-1 flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={clsx(
                                    'h-full rounded-full transition-all',
                                    pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-yellow-500' : 'bg-gray-200'
                                  )}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 w-12 text-right">
                                {data.ready}/{data.total}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            {/* Exam Details */}
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Exam Details</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
                <div>
                  <p className="text-xs text-gray-500">Exam Type</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{upcomingExam.examType?.replace(/_/g, ' ') || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Agency</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{upcomingExam.examinerAgency || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Lead Examiner</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{upcomingExam.leadExaminer || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="mt-1 text-sm font-medium text-gray-900">{upcomingExam.status?.replace(/_/g, ' ') || '—'}</p>
                </div>
              </div>
            </div>

            {/* Action Items */}
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Recommended Actions</h3>
              </div>
              <div className="p-4 space-y-3">
                {(readiness?.openFindings ?? 0) > 0 && (
                  <Link to={`/exams/${upcomingExam.id}`} className="flex items-start gap-3 rounded-lg bg-red-50 p-3 transition-colors hover:bg-red-100">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Resolve {readiness?.openFindings} open finding(s)</p>
                      <p className="mt-0.5 text-xs text-red-600">Open findings from prior exams must be addressed before the next examination.</p>
                    </div>
                  </Link>
                )}
                {(readiness?.unresolvedRequests ?? 0) > 0 && (
                  <Link to={`/exams/${upcomingExam.id}`} className="flex items-start gap-3 rounded-lg bg-yellow-50 p-3 transition-colors hover:bg-yellow-100">
                    <DocumentTextIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Fulfill {readiness?.unresolvedRequests} pending examiner request(s)</p>
                      <p className="mt-0.5 text-xs text-yellow-600">Examiner document requests should be fulfilled promptly.</p>
                    </div>
                  </Link>
                )}
                {(readiness?.readinessScore ?? 0) < 75 && (
                  <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-3">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Prepare remaining document categories</p>
                      <p className="mt-0.5 text-xs text-blue-600">Upload or prepare documents for incomplete categories to improve readiness score.</p>
                    </div>
                  </div>
                )}
                {(readiness?.readinessScore ?? 0) >= 75 && (readiness?.openFindings ?? 0) === 0 && (readiness?.unresolvedRequests ?? 0) === 0 && (
                  <div className="flex items-start gap-3 rounded-lg bg-green-50 p-3">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Good standing</p>
                      <p className="mt-0.5 text-xs text-green-600">Your institution is well-prepared for the upcoming examination.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </FeatureGate>
  );
}
